import { useRouterState } from '@tanstack/react-router';
import { ChevronDown, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { AnimatePresence, LayoutGroup, motion } from 'motion/react';
import { Fragment, useId, useState, type ReactNode } from 'react';

import { useSession } from '@/shared/auth';
import { t } from '@/shared/i18n';
import { cx } from '@/shared/lib/cx';
import { durations, springs } from '@/shared/lib/motion';
import { useSidebar } from '@/shared/store/sidebar';
import { Counter } from '@/shared/ui/Counter';
import { Tooltip } from '@/shared/ui/Tooltip';

import type { SiteScope } from '../site/useSiteScope';

import { Brand } from './Brand';
import { EnvSwitcher } from './EnvSwitcher';
import { useFavorites } from '../site/favorites';

import { navFor, SECTION_LIMIT, visibleEntries, type NavEntry, type NavGroup } from './navigation';
import { RouterAnchor } from './RouterAnchor';
import { SiteSwitcher } from './SiteSwitcher';
import styles from './Sidebar.module.scss';
import { UserMenu } from './UserMenu';

/** Подпись, которая растворяется при сворачивании и не занимает места. */
function Label({
  show,
  children,
  className,
}: {
  show: boolean;
  children: ReactNode;
  className?: string | undefined;
}) {
  return (
    <AnimatePresence initial={false}>
      {show && (
        <motion.span
          className={className}
          initial={{ opacity: 0, x: -4 }}
          animate={{ opacity: 1, x: 0, transition: { delay: 0.08, duration: durations.fast } }}
          exit={{ opacity: 0, transition: { duration: durations.exitFast } }}
        >
          {children}
        </motion.span>
      )}
    </AnimatePresence>
  );
}

/**
 * Боковая колонка — как во flow-do.
 *
 * Наверху — где ты: сайт и окружение (вне сайта — знак Faceta). Ниже —
 * разделы; активный пункт и пункт под курсором отмечаются плывущими
 * подложками. Свёрнутая колонка оставляет иконки с подсказками; в выдвижной
 * панели на телефоне сворачивания нет.
 */
export function Sidebar({
  scope,
  onNavigate,
  collapsible = true,
}: {
  scope: SiteScope | null;
  onNavigate?: () => void;
  collapsible?: boolean;
}) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const session = useSession();
  const groupId = useId();
  const [hovered, setHovered] = useState<string | null>(null);
  const stored = useSidebar((state) => state.collapsed);
  const toggle = useSidebar((state) => state.toggle);
  const folded = useSidebar((state) => state.folded);
  const expanded = useSidebar((state) => state.expanded);
  const toggleFolded = useSidebar((state) => state.toggleFolded);
  const toggleExpanded = useSidebar((state) => state.toggleExpanded);
  const collapsed = collapsible && stored;

  const superAdmin = session.status === 'authenticated' && session.user.is_super_admin;
  const favorites = useFavorites(scope?.siteId);
  const groups = navFor(scope, superAdmin, favorites);

  // Адрес записи коллекцию не содержит, поэтому на экране записи пункт
  // коллекции не подсвечен — где ты, там показывают хлебные крошки.
  const isActive = (entry: NavEntry) =>
    pathname === entry.href || (entry.prefix === true && pathname.startsWith(`${entry.href}/`));

  // Разделы содержимого сворачиваются и укорачиваются; в узкой колонке —
  // нет: там видны одни иконки, а свернуть раздел без заголовка нечем.
  const sectionOf = (group: NavGroup) =>
    group.foldKey === undefined || collapsed
      ? { shown: group.entries, hidden: 0 }
      : visibleEntries(group.entries, {
          folded: folded[group.foldKey] === true,
          expanded: expanded[group.foldKey] === true,
          isActive,
        });

  return (
    <div
      className={cx(
        styles['sidebar'],
        collapsed && styles['sidebar--collapsed'],
        !collapsible && styles['sidebar--drawer'],
      )}
    >
      <div className={styles['sidebar__head']}>
        {scope === null ? (
          <Brand compact={collapsed} />
        ) : (
          <SiteSwitcher scope={scope} compact={collapsed} onNavigate={onNavigate} />
        )}
        {collapsible && (
          <Tooltip content={collapsed ? t.nav.expand : t.nav.collapse} side="right">
            <button
              type="button"
              className={styles['collapse']}
              aria-label={collapsed ? t.nav.expand : t.nav.collapse}
              aria-expanded={!collapsed}
              onClick={toggle}
            >
              {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
            </button>
          </Tooltip>
        )}
      </div>

      {scope !== null && (
        <div className={styles['env']}>
          <EnvSwitcher scope={scope} compact={collapsed} />
        </div>
      )}

      <LayoutGroup id={groupId}>
        <nav
          className={styles['nav']}
          aria-label={t.nav.sections}
          onPointerLeave={() => {
            setHovered(null);
          }}
        >
          {groups.map((group, index) => (
            <Fragment key={group.title ?? `group-${String(index)}`}>
              {/* Перед первой прижатой вниз группой — распорка: она уводит
                  служебные разделы к низу колонки, пока места хватает. */}
              {group.bottom === true && groups[index - 1]?.bottom !== true && (
                <span className={styles['nav__spacer']} aria-hidden="true" />
              )}
              <div className={styles['group']}>
                {group.title !== undefined &&
                  (group.foldKey !== undefined && !collapsed ? (
                    <h2 className={styles['group__title']}>
                      <button
                        type="button"
                        className={styles['group__toggle']}
                        aria-expanded={folded[group.foldKey] !== true}
                        onClick={() => {
                          toggleFolded(group.foldKey ?? '');
                        }}
                      >
                        <span>{group.title}</span>
                        <ChevronDown
                          className={`${styles['group__chevron'] ?? ''} ${
                            folded[group.foldKey] === true
                              ? (styles['group__chevron--folded'] ?? '')
                              : ''
                          }`}
                        />
                      </button>
                    </h2>
                  ) : (
                    <h2 className={styles['group__title']}>
                      <Label show={!collapsed}>{group.title}</Label>
                    </h2>
                  ))}

                {sectionOf(group).shown.map((entry) => {
                  const active = isActive(entry);

                  const link = (
                    <RouterAnchor
                      key={entry.href}
                      href={entry.href}
                      className={styles['nav__item']}
                      aria-current={active ? 'page' : undefined}
                      aria-label={collapsed ? entry.label : undefined}
                      onPointerEnter={() => {
                        setHovered(entry.href);
                      }}
                      onClick={() => {
                        onNavigate?.();
                      }}
                    >
                      {hovered === entry.href && !active && (
                        <motion.span
                          className={styles['nav__hover']}
                          layoutId={`${groupId}-hover`}
                          transition={springs.rubber}
                          aria-hidden="true"
                        />
                      )}
                      {active && (
                        <motion.span
                          className={styles['nav__active']}
                          layoutId={`${groupId}-active`}
                          transition={springs.rubber}
                          aria-hidden="true"
                        />
                      )}
                      <span className={styles['nav__icon']}>{entry.icon}</span>
                      <Label show={!collapsed} className={styles['nav__label']}>
                        {entry.label}
                      </Label>
                      {!collapsed && entry.badge !== undefined && (
                        <Counter
                          value={entry.badge}
                          tone="accent"
                          className={styles['nav__badge']}
                          aria-label={`новых: ${String(entry.badge)}`}
                        />
                      )}
                      {collapsed && entry.badge !== undefined && entry.badge > 0 && (
                        <span className={styles['nav__dot']} aria-hidden="true" />
                      )}
                    </RouterAnchor>
                  );

                  return collapsed ? (
                    <Tooltip key={entry.href} content={entry.label} side="right">
                      {link}
                    </Tooltip>
                  ) : (
                    link
                  );
                })}

                {group.foldKey !== undefined && !collapsed && folded[group.foldKey] !== true && (
                  <MoreToggle
                    hidden={sectionOf(group).hidden}
                    expanded={expanded[group.foldKey] === true}
                    long={group.entries.length > SECTION_LIMIT + 1}
                    onToggle={() => {
                      toggleExpanded(group.foldKey ?? '');
                    }}
                  />
                )}
              </div>
            </Fragment>
          ))}
        </nav>
      </LayoutGroup>

      <div className={styles['sidebar__foot']}>
        <UserMenu compact={collapsed} />
      </div>
    </div>
  );
}

/** «Ещё N» под длинным разделом меню и «Свернуть список» после раскрытия. */
function MoreToggle({
  hidden,
  expanded,
  long,
  onToggle,
}: {
  hidden: number;
  expanded: boolean;
  long: boolean;
  onToggle: () => void;
}) {
  if (!long || (!expanded && hidden === 0)) {
    return null;
  }

  return (
    <button type="button" className={styles['more']} onClick={onToggle}>
      {expanded ? t.nav.showLess : t.nav.showMore(hidden)}
    </button>
  );
}
